import styled, { css, keyframes } from 'styled-components'
import { themeConstants, remCalculator, createCSSObject, createBorder } from '../../../helpers/theming/utilities'
import { themeContract } from './M017.theme.contract'
import { M017StyledPlaceholder, M017StyledCommonTheme, M017StyledNavButton, M017StyledContainer } from './types'

const createHorizontalBorders = (props) => {
  const borderColor =
    props.theme.color.monochrome[themeContract[props.theme.channel].themeMode[props.themeMode].borderColor]
  const topBorder = createBorder(props.theme.size.border.width.sm, borderColor, themeConstants.border.top)
  const bottomBorder = createBorder(props.theme.size.border.width.sm, borderColor, themeConstants.border.bottom)
  return topBorder + bottomBorder
}

const createButtonBorder = (props) => {
  const borderColor =
    props.theme.color.monochrome[themeContract[props.theme.channel].themeMode[props.themeMode].button.borderColor]
  return createBorder('3px', borderColor, themeConstants.border.bottom)
}

const transparent = '#FFFFFF00'

const baseheight = 60 // base value related to anchor positioning

const minWidth = 320 // minumum horizontal space for navbuttons

const maxWidth = 1170 // maximum horizontal space for navbuttons

const navEnter = keyframes`
  0% {transform: translateY(${-baseheight - 1}px);}
  100% {transform: translateY(0);}
`

const navExit = keyframes`
  0% {transform: translateY(0);}
  100% {transform: translateY(${-baseheight - 1}px);}
`

export const StyledPlaceholder = styled.div<M017StyledPlaceholder>`
  height: ${baseheight + 1}px;
  width: 100%;
  margin-bottom: ${(props) => remCalculator(props.theme.size.spacing.lg)};

  ${(props) =>
    props.activeSection &&
    css`
      visibility: hidden;
    `}

  ${(props) =>
    !props.activeSection &&
    css`
      display: none;
    `}
`

export const StyledAnchorPoint = styled.div`
  width: 100%;
  top: ${baseheight + 1}px;

  ${(props) =>
    css`
      @media screen and (min-width: ${props.theme.size.breakpoint['min-width'].md}) {
        top: 0;
      }
    `}
`

export const StyledSubNavWrapper = styled.div<M017StyledCommonTheme>`
  min-width: ${minWidth}px;
  max-width: ${maxWidth}px;
  margin: 0 auto;
  width: 100%;
  min-height: ${baseheight}px;
  text-align: center;
  top: ${remCalculator(-baseheight - 1)};
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  -ms-overflow-style: none;

  ::-webkit-scrollbar {
    display: none;
  }

  &::after {
    pointer-events: none;
    content: '';
    position: absolute;
    height: ${baseheight - 5}px;
    z-index: 75;
    width: 25px;
    right: 0;
    ${(props) =>
      css`
        background: -webkit-linear-gradient(
          right,
          ${props.theme.color.monochrome[themeContract[props.theme.channel].themeMode[props.themeMode].gradientColor]}
            0%,
          ${transparent} 100%
        );
        background: linear-gradient(
          to left,
          ${props.theme.color.monochrome[themeContract[props.theme.channel].themeMode[props.themeMode].gradientColor]}
            0%,
          ${transparent} 100%
        );
      `}
  }

  ${(props) =>
    css`
      ${createHorizontalBorders(props)}
      background: ${props.theme.color.monochrome[
        themeContract[props.theme.channel].themeMode[props.themeMode].background
      ]};

      @media screen and (min-width: ${props.theme.size.breakpoint['min-width'].lg}) {
        top: 0;

        &::after {
          display: none;
        }
      }
    `}
`

export const StyledNavButtonWrapper = styled.div`
  width: 100%;
  min-width: ${minWidth}px;
  max-width: ${maxWidth}px;
  display: flex;
  flex-direction: row;
  align-items: center;
  position: relative;
  overflow-x: scroll;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  ${(props) =>
    css`
      margin: 0 ${remCalculator(-parseInt(props.theme.size.spacing.lg, 10))};

      &::after {
        content: '';
        min-width: ${props.theme.size.spacing.lg};
        display: block;
        height: ${baseheight - 3}px;

        @media screen and (min-width: ${props.theme.size.breakpoint['min-width'].lg}) {
          display: none;
        }
      }
    `}
`

export const StyledButtonDivider = styled.span<M017StyledCommonTheme>`
  height: 24px;
  width: 1px;
  position: absolute;
  right: 0;

  ${(props) =>
    css`
      background: ${props.theme.color.monochrome[
        themeContract[props.theme.channel].themeMode[props.themeMode].button.divider.background
      ]};
      top: ${remCalculator(props.theme.size.spacing.lg)};
    `}
`

export const StyledNavButton = styled.button<M017StyledNavButton>`
  white-space: nowrap;
  height: ${baseheight - 3}px;
  background-color: transparent;
  margin: 0;
  border: none;
  outline: none;
  position: relative;
  flex-grow: 1;
  flex-basis: 0;

  &::after {
    content: '';
    width: ${(props) => (props.isActive ? `calc(100% - 1px)` : 0)};
    position: absolute;
    left: 0;
    bottom: 0;
    transform: scaleX(${(props) => (props.isActive ? 1 : 0)});
    transition: transform 0.25s ease-in-out;
    transition-delay: 0.15s;
    display: block;
    ${(props) => props.isActive && createButtonBorder(props)}
  }

  &:hover {
    color: ${(props) =>
      props.theme.color.monochrome[themeContract[props.theme.channel].themeMode[props.themeMode].button.hoverColor]};
    ${(props) =>
      themeContract[props.theme.channel].themeMode[props.themeMode].rollover &&
      !props.isActive &&
      css`
        ::after {
          transform: scaleX(1);
          width: calc(100% - 1px);
          ${createButtonBorder(props)}
        }
      `}
  }

  ${(props) =>
    css`
      ${createCSSObject(props.theme.font.stack['label-1'])}
      font-weight: 400; // override for now as token weight is currently too bold
      padding: 0 ${remCalculator(props.theme.size.spacing.xl)};
      color: ${props.theme.color.monochrome[
        themeContract[props.theme.channel].themeMode[props.themeMode].button.fontColor
      ]};

      @media screen and (min-width: ${props.theme.size.breakpoint['min-width'].lg}) {
        overflow: hidden;
        text-overflow: ellipsis;
        justify-content: center;
      }
    `}

  &:last-child {
    ${StyledButtonDivider} {
      display: none;
    }
  }
`

export const StyledContainer = styled.div<M017StyledContainer>`
  width: 100%;
  top: ${-baseheight - 1}px; // no equivalent spacing token

  ${(props) =>
    css`
      @media screen and (min-width: ${props.theme.size.breakpoint['min-width'].md}) {
        top: 0;
      }
      @media screen and (max-width: ${props.theme.size.breakpoint['max-width'].md}) {
        margin-bottom: ${props.theme.size.spacing.lg};
      }
    `}

    ${(props) =>
      props.isFixedPosition &&
      css`
        will-change: transform;
        z-index: 75;
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        animation-name: ${navEnter};
        animation-duration: 0.5s;
        animation-fill-mode: forwards;

        ${StyledSubNavWrapper} {
          &::after {
            right: ${-parseInt(props.theme.size.spacing.lg, 10)}px;
          }

          @media screen and (min-width: ${props.theme.size.breakpoint['min-width'].lg}) {
            width: calc(100% - 30px);
          }
        }
      `}

    ${(props) =>
      !props.isFixedPosition &&
      css`
        margin: 0 ${-props.theme.size.spacing.lg} ${props.theme.size.spacing.lg};
        min-width: calc(100% + ${props.theme.size.spacing.lg * 2});

        @media screen and (min-width: ${props.theme.size.breakpoint['min-width'].lg}) {
          margin: 0 0 ${props.theme.size.spacing.lg};
          min-width: 100%;
        }

        ${StyledNavButton}::after {
          border: none;
          display: none;
        }
      `}

    ${(props) =>
      props.isFixedBelow &&
      css`
        animation-name: ${navExit};
        animation-duration: 0.5s;
        animation-fill-mode: forwards;
      `}

    &::before {
      content: '';
      display: block;
      margin: 0 auto;
      height: ${baseheight - 2}px;
      position: absolute;
      transition: transform 0.5s ease-out;
      ${(props) =>
        css`
          background: ${props.theme.color.monochrome[
            themeContract[props.theme.channel].themeMode[props.themeMode].background
          ]};
          ${createHorizontalBorders(props)}
        `}
      ${(props) =>
        props.isFixedPosition
          ? css`
              width: 100%;
              transform: scaleX(1);
              left: 0;
            `
          : css`
              width: 10%;
              transform: scaleX(0.1);
              left: 45%;
            `}
    }
  }

  ${StyledNavButton}:first-child {
    margin-left: ${(props) => props.theme.size.spacing.md};

    ${(props) =>
      css`
        @media screen and (min-width: ${props.theme.size.breakpoint['min-width'].lg}) {
          margin-left: 0;
        }
      `}
  }
`
